"use client";

import { useEffect, useMemo, useRef } from "react";
import {
    NextIntlClientProvider,
    useLocale as useNextIntlLocale,
    useTranslations as useNextIntlTranslations,
} from "next-intl";

import type { Locale } from "./config";
import type { Messages } from "./dictionaries";

type I18nProviderProps = {
    locale: Locale;
    messages: Messages;
    fallbackMessages: Messages;
    children: React.ReactNode;
};

function mergeDeep(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
    const output: Record<string, unknown> = { ...target };
    for (const [key, value] of Object.entries(source)) {
        if (
            value !== null &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            typeof output[key] === "object" &&
            output[key] !== null &&
            !Array.isArray(output[key])
        ) {
            output[key] = mergeDeep(output[key] as Record<string, unknown>, value as Record<string, unknown>);
        } else if (output[key] === undefined) {
            output[key] = value;
        }
    }
    return output;
}

export function I18nProvider({ locale, messages, fallbackMessages, children }: I18nProviderProps) {
    const merged = useMemo(
        () => mergeDeep(messages as Record<string, unknown>, fallbackMessages as Record<string, unknown>),
        [messages, fallbackMessages]
    );
    const localeRef = useRef<string | null>(null);

    useEffect(() => {
        if (localeRef.current !== locale) {
            document.documentElement.lang = locale;
            localeRef.current = locale;
        }
    }, [locale]);

    return (
        <NextIntlClientProvider
            locale={locale}
            messages={merged as Record<string, unknown>}
            onError={() => {}}
            getMessageFallback={({ key, namespace }) => (namespace ? `${namespace}.${key}` : key)}
        >
            {children}
        </NextIntlClientProvider>
    );
}

type InterpolationValues = Record<string, string | number>;

export function useTranslations(namespace?: string) {
    const t = useNextIntlTranslations(namespace);
    return (key: string, values?: InterpolationValues): string => {
        try {
            return t(key, values);
        } catch {
            return namespace ? `${namespace}.${key}` : key;
        }
    };
}

export function useCurrentLocale(): Locale {
    return useNextIntlLocale() as Locale;
}
